import * as React from 'react';

import {Stack, Typography} from '@mui/material';
import {AggregationSurveyScoreSeries} from 'crqlar-react-common/dist/models/aggregation/surveyAggregation.model';
import {useTranslation} from 'react-i18next';

import {CardHeadline} from '../../../insights/kit/Headline';
import {CHART_HEIGHT} from "../../../insights/kit/insightsChart";
import {SeriesCard} from '../../../insights/kit/SeriesCard';
import {calendarDayToTimestamp, formatFullDate, useInsightsFormatters} from "../../../insights/kit/insightsFormat";
import {InsightsLoading} from '../../../insights/kit/insightsLoading';
import {InsightsSeriesChart, InsightsSeriesLegend, SeriesColors, SeriesSpec} from '../charts/InsightsSeriesChart';
import {InsightsColumn, InsightsTable} from '../../../insights/kit/InsightsTable';
import {TooltipDelta, TooltipRow} from '../../../insights/kit/InsightsTooltip';

const MIN_SCORE = 0;
const MAX_SCORE = 100;

const AXIS_PADDING = 5;
const AXIS_STEP = 10;
const MIN_AXIS_SPAN = 20;

interface ScoreRow {
    index: number;
    timestamp: number | null;
    compareTimestamp: number | null;
    score: number | null;
    compare: number | null;
    responses: number | null;
    /** Foot of the plot on the days nothing was scored; null everywhere else. */
    baseline: number | null;
}

interface CrqlarScoreCardProps {
    series?: AggregationSurveyScoreSeries;
    loading?: InsightsLoading;
    height?: number;
}

function toRows(series: AggregationSurveyScoreSeries | undefined): ScoreRow[] {
    if (series == null) return [];

    const current = series.current.points;
    const compare = series.compare?.points ?? [];
    const length = Math.max(current.length, compare.length);

    return Array.from({length}, (_, index) => {
        const point = current[index];
        const comparePoint = compare[index];

        return {
            index,
            timestamp: point == null ? null : calendarDayToTimestamp(point.day),
            compareTimestamp: comparePoint == null ? null : calendarDayToTimestamp(comparePoint.day),
            score: point?.value ?? null,
            compare: comparePoint?.value ?? null,
            responses: point?.responses ?? null,
            baseline: null,
        };
    });
}

function axisFor(values: number[]): {domain: [number, number]; ticks: number[]} {
    if (values.length === 0) {
        return {domain: [MIN_SCORE, MAX_SCORE], ticks: [0, 25, 50, 75, 100]};
    }

    let low = Math.floor((Math.min(...values) - AXIS_PADDING) / AXIS_STEP) * AXIS_STEP;
    let high = Math.ceil((Math.max(...values) + AXIS_PADDING) / AXIS_STEP) * AXIS_STEP;

    while (high - low < MIN_AXIS_SPAN) {
        if (low > MIN_SCORE) low -= AXIS_STEP;
        else high += AXIS_STEP;
    }

    low = Math.max(MIN_SCORE, low);
    high = Math.min(MAX_SCORE, high);

    const ticks: number[] = [];
    for (let tick = low; tick <= high; tick += AXIS_STEP) ticks.push(tick);

    return {domain: [low, high], ticks};
}

interface ScoreTableLabels {
    date: string;
    compareDate: string;
    score: string;
    compare: string;
    compareGap: string;
    target: string;
    targetGap: string;
}

interface ScoreTableProps {
    rows: ReadonlyArray<ScoreRow>;
    labels: ScoreTableLabels;
    hasCompare: boolean;
    target?: number;
    formatScore: (score: number) => string;
}

function ScoreTable({rows, labels, hasCompare, target, formatScore}: ScoreTableProps) {
    const ordered = React.useMemo(() => [...rows].reverse(), [rows]);

    const targetGap = (row: ScoreRow) => (target == null || row.score == null ? null : row.score - target);
    const compareGap = (row: ScoreRow) => (row.score == null || row.compare == null ? null : row.score - row.compare);

    const columns: Array<InsightsColumn<ScoreRow> | false> = [
        {key: 'date', kind: 'label', label: labels.date, cell: row => (row.timestamp == null ? '—' : formatFullDate(row.timestamp))},
        {key: 'score', kind: 'value', label: labels.score, cell: row => row.score, format: formatScore},
        hasCompare && {
            key: 'compareDate',
            kind: 'text',
            label: labels.compareDate,
            cell: row => (row.compareTimestamp == null ? '—' : formatFullDate(row.compareTimestamp)),
            comparison: true,
        },
        hasCompare && {key: 'compare', kind: 'compare', label: labels.compare, cell: row => row.compare, format: formatScore},
        hasCompare && {
            key: 'compareGap',
            kind: 'delta',
            label: labels.compareGap,
            cell: compareGap,
            format: formatScore,
            comparison: true,
        },
        target != null && {key: 'target', kind: 'detail', label: labels.target, cell: () => target, format: formatScore},
        target != null && {key: 'targetGap', kind: 'delta', label: labels.targetGap, cell: targetGap, format: formatScore},
    ];

    return <InsightsTable label={labels.score} columns={columns} rows={ordered} rowKey={row => row.index}/>;
}

export function CrqlarScoreCard({series, loading, height = CHART_HEIGHT}: CrqlarScoreCardProps) {
    const {t} = useTranslation();

    const rows = React.useMemo(() => toRows(series), [series]);

    const hasCompare = series?.compare != null;
    const target = series?.summary.target ?? undefined;
    const windowDays = series?.windowDays ?? 7;

    const format = useInsightsFormatters();

    const scoreLabel = t('Crqlar score');
    const compareLabel = t('Compare period');

    const tableLabels = React.useMemo<ScoreTableLabels>(
        () => ({
            date: t('Date'),
            compareDate: t('Compare date'),
            score: scoreLabel,
            compare: compareLabel,
            compareGap: t('Compare gap'),
            target: t('Target'),
            targetGap: t('Target gap'),
        }),
        [compareLabel, scoreLabel, t],
    );

    const axis = React.useMemo(() => {
        const values = rows.flatMap(row => [row.score, row.compare]).filter((value): value is number => value != null);
        if (target != null) values.push(target);

        return axisFor(values);
    }, [rows, target]);

    /*
     * A day the range covers but nothing was scored on gets a mark at the foot of the plot, so a
     * single reply in a quiet month is reached by a line rather than left floating on its own.
     */
    const chartRows = React.useMemo(
        () => rows.map(row => ({
            ...row,
            baseline: row.score == null && row.timestamp != null ? axis.domain[0] : null,
        })),
        [axis.domain, rows],
    );

    const current = series?.summary.value ?? null;
    const previous = series?.summary.previous ?? null;
    const change = current == null || previous == null ? null : current - previous;

    const headline = (
        <CardHeadline
            value={current == null ? '—' : format.whole(current)}
            change={previous == null ? null : {
                title: scoreLabel,
                current: current == null ? '—' : format.whole(current),
                previous: format.whole(previous),
                delta: change,
                formatDelta: format.whole,
                compareLabel,
            }}
        />
    );

    const tooltipRows = (row: ScoreRow, colors: SeriesColors) => {
        const gap = target == null || row.score == null ? null : row.score - target;

        // Nothing came in that day, so there is no score to state - saying "0" would invent one.
        if (row.score == null && row.compare == null) {
            return <TooltipRow label={scoreLabel} value={t('No responses')}/>;
        }

        return (
            <React.Fragment>
                {row.score != null && (
                    <TooltipRow swatch={colors.series} label={scoreLabel} value={format.decimal(row.score)}/>
                )}

                {row.compare != null && (
                    <TooltipRow
                        swatch={colors.compare}
                        label={
                            row.compareTimestamp == null
                                ? compareLabel
                                : `${compareLabel} · ${formatFullDate(row.compareTimestamp)}`
                        }
                        value={format.decimal(row.compare)}
                    />
                )}

                {row.responses != null && <TooltipRow label={t('Replies in window')} value={String(row.responses)}/>}

                {gap != null && (
                    <TooltipDelta swatch={colors.target} label={t('Target gap')} value={gap} format={format.decimal}/>
                )}
            </React.Fragment>
        );
    };

    const spec: SeriesSpec<ScoreRow> = {
        rows: chartRows,
        valueKey: 'score',
        compareKey: hasCompare ? 'compare' : undefined,
        label: scoreLabel,
        compareLabel,
        target: target == null
            ? undefined
            : {
                value: target,
                verdict: 'whole',
                label: `${t('Target')} ${format.whole(target)}`,
                // The headline is the whole range, so the area is judged on it rather than on its last day.
                actual: current,
            },
    };

    return (
        <SeriesCard
            title={scoreLabel}
            hint={t('The Crqlar score is answer quality, on 0 to 100. Each point is the score of the {{days}} days ending on it, so one quiet day does not swing the line; the headline is the whole range. The dashed line is the compare period, laid alongside day by day.', {days: windowDays})}
            headline={headline}
            loading={loading}
            legend={<InsightsSeriesLegend {...spec}/>}
            empty={rows.length === 0 ? (
                <Typography color={'text.secondary'} variant={'body2'}>
                    {t('No score data available.')}
                </Typography>
            ) : undefined}
            chart={(
                <InsightsSeriesChart
                    {...spec}
                    height={height}
                    domain={axis.domain}
                    ticks={axis.ticks}
                    formatTick={format.whole}
                    tooltip={tooltipRows}
                    connectNulls
                    baselineKey={'baseline'}
                />
            )}
            table={(
                <ScoreTable
                    rows={rows}
                    labels={tableLabels}
                    hasCompare={hasCompare}
                    target={target}
                    formatScore={format.decimal}
                />
            )}
        />
    );
}
